/* eslint-disable no-plusplus */
import { dropRight, max, min, range, sum, uniqBy } from 'lodash';
import * as mobx from 'mobx';
import { getSnapshot, types as t } from 'mobx-state-tree';
import { useStore } from 'mobx-store-provider';
import { Editor, Transforms } from 'slate';

import { ELEMENT_H3 } from '../../../../../../slatePlugins';
import {
  ELEMENT_ALIGN_CENTER,
  ELEMENT_ALIGN_LEFT,
  ELEMENT_ALIGN_RIGHT,
} from '../../../../../../slatePlugins/elements/align/defaults';
import { ELEMENT_PARAGRAPH } from '../../../../../../slatePlugins/elements/paragraph/defaults';
import { TableElement } from '../../../table/types';
import { createTd, createTr } from '../../../table/utils';
import { removeTable } from '../../transforms/removeTable';
import { TableAdminViewCellSelectionStore } from './table-adminview-selection.store';
import { isMouseEvent, isTouchEvent } from './types';

export const getInitialStateTableAdminView = (data: any) => {
  const result: any = {
    id: data.id,
    width: data.width || 0,
    cellsWidth: data?.cellsWidth?.map((cellWidth) => {
      if (typeof cellWidth === 'string') {
        cellWidth = Number(cellWidth.replace('px', ''));
      }
      return cellWidth;
    }) || [0, 0, 0],
    rowsHeight: range(data?.children?.length || 3).map(() => 0),
  };

  result.hasColumnHeading = Boolean(data.hasColumnHeading);
  result.hasRowHeading = Boolean(data.hasRowHeading);
  result.hasBorder = Boolean(data.border);
  result.isH3Enabled = Boolean(data.isH3Enabled);

  return result;
};

const initialRangeSelection = {
  colIdx: -1,
  rowIdx: -1,
};

const initialStateSelectState = {
  hovered: initialRangeSelection,
  selected: initialRangeSelection,
  hoveredResizeDecoration: initialRangeSelection,
};

const RangeSelection = t.model('RangeSelection', {
  colIdx: t.optional(t.number, -1),
  rowIdx: t.optional(t.number, -1),
});

const TableAdminViewSelectStateStore = t
  .model('TableAdminViewSelectStateStore', {
    hovered: t.optional(RangeSelection, initialRangeSelection),
    hoveredResizeDecoration: t.optional(RangeSelection, initialRangeSelection),
    isHoverOnDelete: t.optional(t.boolean, false),
    isHoverOnAddButton: t.optional(t.boolean, false),
    addButtonColIdx: t.optional(t.number, -1),
    addButtonRowIdx: t.optional(t.number, -1),

    isTableDeleteHovered: t.optional(t.boolean, false),
  })
  .actions((self) => ({
    hoverResizeColumn(colIdx) {
      self.hoveredResizeDecoration.colIdx = colIdx;
    },
    stopHoverResizeColumn() {
      self.hoveredResizeDecoration = initialRangeSelection;
    },

    unselectAll() {
      self.hovered = initialRangeSelection;
      self.isHoverOnDelete = false;
    },

    hoverOnDelete() {
      self.isHoverOnDelete = true;
    },
    stopHoverOnDelete() {
      self.isHoverOnDelete = false;
    },
    hoverOnAddButton() {
      self.isHoverOnAddButton = true;
    },
    stopHoverOnAddButton() {
      self.isHoverOnAddButton = false;
      self.addButtonColIdx = -1;
      self.addButtonRowIdx = -1;
    },
    hoverDeleteTable() {
      self.isTableDeleteHovered = true;
    },
    stopHoverDeleteTable() {
      self.isTableDeleteHovered = false;
    },
  }));

export const TableAdminViewStore = t
  .model('TableAdminViewStore', {
    id: t.string,
    width: t.number,
    cellsWidth: t.array(t.number),
    rowsHeight: t.array(t.number),
    hasBorder: t.boolean,
    selection: t.optional(TableAdminViewSelectStateStore, initialStateSelectState),
    caption: t.optional(t.string, ''),

    // resize
    isResizing: false,
    resizeX: t.optional(t.number, -1),
    resizeColumnIndex: t.optional(t.number, -1),
    originalWidth: t.optional(t.number, -1),

    addButtonLeft: t.optional(t.number, -1),
    addButtonTop: t.optional(t.number, -1),

    // info
    hasColumnHeading: t.optional(t.boolean, false),
    hasRowHeading: t.optional(t.boolean, false),
    isInTable: t.optional(t.boolean, false),
    isH3Enabled: t.optional(t.boolean, false),

    // merge cell selection
    cellSelection: t.optional(TableAdminViewCellSelectionStore, {
      isDrag: false,
      isPressShiftKey: false,
    }),
  })
  .volatile(() => ({
    tableNode: null,
    tablePath: null,
    isConfirmMergeVisible: false,
    tempStartDragPosition: null,
    tempEndDragPosition: null,
  }))
  .views((self) => ({
    get rowCount() {
      return self.rowsHeight.map((n) => n).length;
    },
    get columnCount() {
      return self.cellsWidth.map((n) => n).length;
    },
  }))
  .views((self) => ({
    get isAbleToDeleteRow() {
      return self.rowCount > 1;
    },
    get isAbleToDeleteColumn() {
      return self.columnCount > 1;
    },
    get isShowAddButton() {
      return (
        self.selection.isHoverOnAddButton ||
        self.cellSelection.startHoverPosition ||
        self.cellSelection.endHoverPosition
      );
    },
    get tableHeight() {
      return sum(self.rowsHeight.map((item) => item + 0));
    },
    get tableWidth() {
      return sum(self.cellsWidth.map((item) => item + 0));
    },
    getCellWidthByColIndex(columnIndex) {
      return self.cellsWidth[columnIndex];
    },
    getFirstCellOfRow({ editor, rowIndex }) {
      const [[trNode]] = Editor.nodes(editor, {
        at: [...self.tablePath, rowIndex],
        match: (node: any) => node?.type === TableElement.TR,
      });
      return trNode.children[0];
    },
  }))
  .actions((self) => ({
    updateCellWidthToSlate({ editor }) {
      Transforms.setNodes(
        editor,
        { cellsWidth: mobx.toJS(self.cellsWidth), width: mobx.toJS(self.tableWidth) },
        { at: self.tablePath }
      );
    },
    updateSlateToCellWidth() {
      self.cellsWidth = self.tableNode.cellsWidth;
    },
    updateRowHeight() {
      const tableElm: any = document.getElementById(self.id);
      if (tableElm?.rows) {
        self.rowsHeight.replace(
          Array.from(tableElm.rows).map((elm: any) => {
            return elm.clientHeight as number;
          })
        );
      }
    },
    updateCaption({ editor, caption }) {
      Transforms.setNodes(editor, { caption }, { at: self.tablePath });
      self.caption = caption;
    },
    syncRowColRef({ editor }) {
      // Clear all hidden, rowRef, colRef
      Transforms.unsetNodes(editor, ['hidden', 'rowRef', 'colRef'], {
        at: self.tablePath,
        match: (n: any) => [TableElement.TH, TableElement.TD].includes(n?.type),
      });
      const nodeEntries: any = Editor.nodes(editor, {
        at: self.tablePath,
        match: (n: any) => [TableElement.TH, TableElement.TD].includes(n?.type),
      });
      try {
        for (const [node, path] of nodeEntries) {
          if (node?.rowSpan > 1 || node?.colSpan > 1) {
            const tablePath = dropRight(path, 2) as any;
            const rowPath = dropRight(path, 1);
            const rowIndex = rowPath[rowPath.length - 1] as number;
            const cellIndex = path[path.length - 1] as number;
            const rowSpan = node?.rowSpan ? node.rowSpan - 1 : 1;
            const colSpan = node?.colSpan ? node.colSpan - 1 : 1;
            range(rowIndex, rowIndex + 1 + rowSpan).forEach((rIdx) => {
              range(cellIndex, cellIndex + 1 + colSpan).forEach((cIdx) => {
                if (!(cIdx === cellIndex && rowIndex === rIdx)) {
                  try {
                    Transforms.setNodes(
                      editor,
                      { hidden: true, rowRef: rowIndex, colRef: cellIndex },
                      { at: [...tablePath, rIdx, cIdx] }
                    );
                    // eslint-disable-next-line no-empty
                  } catch (ex) {}
                }
              });
            });
          }
        }
      } catch (ex) {
        console.log('syncRowColRef error', ex.message);
      }
    },
  }))
  .actions((self) => ({
    setAddButtonPosition({ left, top, colIndex = -1, rowIndex = -1 }) {
      self.addButtonLeft = left;
      self.addButtonTop = top;
      self.selection.addButtonColIdx = colIndex;
      self.selection.addButtonRowIdx = rowIndex;
    },
    onResizeStart(event, resizeColumnIndex) {
      let clientX = 0;
      if (event.nativeEvent && isMouseEvent(event.nativeEvent)) {
        clientX = event.nativeEvent.clientX;
        // When user click with right button the resize is stuck in resizing mode
        // until users clicks again, dont continue if right click is used.
        if (event.nativeEvent.which === 3) {
          return;
        }
      } else if (event.nativeEvent && isTouchEvent(event.nativeEvent)) {
        clientX = (event.nativeEvent as TouchEvent).touches[0].clientX;
      }
      self.isResizing = true;
      self.resizeX = clientX;
      self.resizeColumnIndex = resizeColumnIndex;
      self.originalWidth = self.cellsWidth[resizeColumnIndex] || 0;
    },
    resizeFrame(event, editor) {
      if (self.isResizing && self.resizeX >= 0) {
        const xDiff = Math.abs(self.resizeX - event.clientX);
        let newW =
          self.resizeX > event.clientX ? self.originalWidth - xDiff : self.originalWidth + xDiff;

        if (newW < 80) {
          newW = 80;
        }
        const [tableNode] = Editor.node(editor, self.tablePath) as any;
        const cloneCellWidths = [...tableNode.cellsWidth];
        cloneCellWidths[self.resizeColumnIndex] = newW;
        self.cellsWidth.replace(cloneCellWidths);
      }
      return null;
    },
    stopResize({ editor }) {
      if (self.isResizing) {
        self.isResizing = false;
        self.resizeColumnIndex = -1;
        self.resizeX = -1;

        self.updateCellWidthToSlate({ editor });
      }
    },

    syncInfo({ editor, manual }) {
      if (editor?.selection || manual) {
        const nodeEntries = Editor.nodes(editor, {
          at: manual ? [] : editor.selection,
          match: (n: any) => n?.type === TableElement.TABLE && n.id === self.id,
        });
        try {
          if (nodeEntries) {
            const [tableNode, tablePath] = Array.from(nodeEntries)[0] as any;
            self.isInTable = true;
            self.hasColumnHeading = Boolean(tableNode.hasColumnHeading);
            self.hasRowHeading = Boolean(tableNode.hasRowHeading);
            self.hasBorder = Boolean(tableNode.border);
            self.isH3Enabled = Boolean(tableNode.isH3Enabled);
            self.caption = tableNode.caption;
            self.tableNode = tableNode;
            self.tablePath = tablePath;
            self.cellsWidth = (tableNode?.cellsWidth || []).map((cellWidth) => {
              if (typeof cellWidth === 'string') {
                return Number(cellWidth.replace('px', '')) || 213;
              }
              return cellWidth;
            });
            self.updateRowHeight();
            return;
          }
          // eslint-disable-next-line no-empty
        } catch (ex) {}
      }
      self.isInTable = false;
    },
  }))
  .actions((self) => ({
    addCol({ editor }) {
      const colIdx = self.selection.addButtonColIdx;
      if (colIdx < 0) return;

      let colSpanBeforeInsert = 1;
      try {
        const [firstCell] = Editor.node(editor, [...self.tablePath, 0, colIdx]) as any;

        if (firstCell) {
          if (firstCell.colSpan > 1) {
            colSpanBeforeInsert = firstCell.colSpan;
          }
          if (firstCell.hidden) {
            const [cellRef] = Editor.node(editor, [
              ...self.tablePath,
              firstCell.rowRef,
              firstCell.colRef,
            ]) as any;
            if (cellRef.colSpan > 1) {
              colSpanBeforeInsert = cellRef.colSpan;
            }
          }
        }
        // eslint-disable-next-line no-empty
      } catch (ex) {}
      if (colIdx + (colSpanBeforeInsert - 1) > self.columnCount - 1) {
        // insert new empty column
        range(0, self.rowCount - 1).forEach((rowIndex) => {
          Transforms.insertNodes(editor, createTd({}), {
            at: [...self.tablePath, rowIndex, colIdx],
          });
        });
      } else {
        const cloneCells = range(0, self.rowCount).map((rowIndex) => {
          const cellPath = [...self.tablePath, rowIndex, colIdx];
          return Editor.node(editor, cellPath);
        });

        let baseCells = [];

        cloneCells.forEach((cellNode) => {
          const [node, path] = cellNode as any;
          const rowPath = dropRight(path, 1) as any;

          let newCell = createTd({});

          if (node.hidden) {
            const [cellRef] = Editor.node(editor, [
              ...self.tablePath,
              node.rowRef,
              node.colRef,
            ]) as any;
            if (cellRef.colSpan > 1) {
              newCell = createTd({
                hidden: node.hidden,
                rowRef: node.rowRef,
                colRef: node.colRef,
              });
              baseCells.push({ columnIndex: node.colRef, rowIndex: node.rowRef });
            }
          }

          Transforms.insertNodes(editor, newCell, {
            at: [...rowPath, colIdx],
          });
        });

        baseCells = uniqBy(baseCells, (o) => `${o.columnIndex}-${o.rowIndex}`);
        if (baseCells.length > 0) {
          baseCells.forEach(({ columnIndex, rowIndex }) => {
            const [cellRef] = Editor.node(editor, [
              ...self.tablePath,
              rowIndex,
              columnIndex,
            ]) as any;
            Transforms.setNodes(
              editor,
              { colSpan: cellRef.colSpan + 1 },
              { at: [...self.tablePath, rowIndex, columnIndex] }
            );
          });
        }
      }

      self.cellsWidth.push(200);
      self.updateCellWidthToSlate({ editor });
      self.syncInfo({ editor, manual: true });
      self.syncRowColRef({ editor });
    },
    addRow({ editor }) {
      const rowIdx = self.selection.addButtonRowIdx;
      if (rowIdx < 0) return;

      if (rowIdx >= self.rowCount) {
        const newRow = createTr(self.columnCount);
        Transforms.insertNodes(editor, newRow, { at: [...self.tablePath, rowIdx] });
      } else {
        const cloneRow = Editor.node(editor, [...self.tablePath, rowIdx]); // from slate

        if (cloneRow) {
          const [rowNode] = cloneRow;
          const newRow = createTr(rowNode.children.length); // create empty row -> [empty cell, empty cell, empty cell]

          let baseCells = [];

          rowNode.children.forEach((colNode, colIndex) => {
            if (colNode.hidden) {
              const [cellRef] = Editor.node(editor, [
                ...self.tablePath,
                colNode.rowRef,
                colNode.colRef,
              ]) as any;

              if (cellRef.rowSpan > 1) {
                (newRow.children[colIndex] as any).hidden = colNode.hidden;
                (newRow.children[colIndex] as any).rowRef = colNode.rowRef;
                (newRow.children[colIndex] as any).colRef = colNode.colRef;
                baseCells.push({ columnIndex: colNode.colRef, rowIndex: colNode.rowRef });
              }
            }
          });
          baseCells = uniqBy(baseCells, (o) => `${o.columnIndex}-${o.rowIndex}`);
          if (baseCells.length > 0) {
            baseCells.forEach(({ columnIndex, rowIndex }) => {
              const [cellRef] = Editor.node(editor, [
                ...self.tablePath,
                rowIndex,
                columnIndex,
              ]) as any;
              Transforms.setNodes(
                editor,
                { rowSpan: cellRef.rowSpan + 1 },
                { at: [...self.tablePath, rowIndex, columnIndex] }
              );
            });
          }

          Transforms.insertNodes(editor, newRow, { at: [...self.tablePath, rowIdx] });
        }
      }

      self.syncInfo({ editor, manual: true });
      self.syncRowColRef({ editor });
    },
    removeCol({ editor }) {
      range(
        min(self.cellSelection.selectedColumnIndexes),
        max(self.cellSelection.selectedColumnIndexes) + 1
      ).forEach((columnIndex) => {
        if (columnIndex >= 0) {
          if (self.columnCount <= 1) {
            removeTable(editor);
          } else {
            const rowNodeEntries = Editor.nodes(editor, {
              at: self.tablePath,
              match: (n: any) => n?.type === TableElement.TR,
            });

            // Deleting a column of a row conflicts with normalization table logic ( all row should have same number of columns )
            // So we need to disable normalizing during removing columns & enable back afterward.
            Editor.withoutNormalizing(editor, () => {
              for (const [, rowPath] of rowNodeEntries) {
                Transforms.removeNodes(editor, {
                  at: [...rowPath, columnIndex],
                });
              }
              self.cellsWidth.splice(columnIndex, 1);
              self.updateCellWidthToSlate({ editor });
            });
          }
        }
      });
      self.syncInfo({ editor, manual: true });
      self.syncRowColRef({ editor });
    },
    removeRow({ editor }) {
      range(
        min(self.cellSelection.selectedRowIndexes),
        max(self.cellSelection.selectedRowIndexes) + 1
      ).forEach((rowIndex) => {
        if (rowIndex >= 0) {
          Transforms.removeNodes(editor, {
            at: [...self.tablePath, rowIndex],
          });
          const newPath = Editor.start(editor, [...self.tablePath, Math.max(rowIndex - 1, 0)]);
          Transforms.select(editor, newPath);
          Transforms.collapse(editor, { edge: 'end' });
          self.selection.unselectAll();
          // Should wait for slate to re-render table before counting rows
          setTimeout(() => {
            self.updateRowHeight();
          }, 1);
        }
      });
      self.syncInfo({ editor, manual: true });
      self.syncRowColRef({ editor });
    },
    toggleRowHeading({ editor }) {
      Transforms.setNodes(editor, { hasRowHeading: !self.hasRowHeading }, { at: self.tablePath });
      self.hasRowHeading = !self.hasRowHeading;
    },
    toggleColumnHeading({ editor }) {
      Transforms.setNodes(
        editor,
        { hasColumnHeading: !self.hasColumnHeading },
        { at: self.tablePath }
      );
      self.hasColumnHeading = !self.hasColumnHeading;
    },
    setBordered({ editor }) {
      Transforms.setNodes(editor, { border: true }, { at: self.tablePath });
      self.hasBorder = true;
    },
    setNoBordered({ editor }) {
      Transforms.setNodes(editor, { border: false }, { at: self.tablePath });
      self.hasBorder = false;
    },
    cellAlignLeft({ editor }) {
      self.cellSelection.allSelectedCells.forEach((cellPath) => {
        Transforms.setNodes(editor, { type: ELEMENT_ALIGN_LEFT }, { at: [...cellPath, 0] });
      });
    },
    cellAlignCenter({ editor }) {
      self.cellSelection.allSelectedCells.forEach((cellPath) => {
        Transforms.setNodes(editor, { type: ELEMENT_ALIGN_CENTER }, { at: [...cellPath, 0] });
      });
    },
    cellAlignRight({ editor }) {
      self.cellSelection.allSelectedCells.forEach((cellPath) => {
        Transforms.setNodes(editor, { type: ELEMENT_ALIGN_RIGHT }, { at: [...cellPath, 0] });
      });
    },
    clearCell({ editor }) {
      self.cellSelection.allSelectedCells.forEach((cellPath) => {
        Transforms.insertText(editor, '', {
          at: [...cellPath, 0],
        });
      });
    },
    toggleH3Enabled({ editor, isH3Enabled }) {
      self.cellSelection.allSelectedCells.forEach((cellPath) => {
        let finalPath = cellPath;
        let [currentNode] = Editor.node(editor, cellPath) as any[];
        if ([TableElement.TD, TableElement.TH].includes(currentNode?.type)) {
          currentNode = currentNode.children[0];
          finalPath = [...cellPath, 0];
        }
        if (currentNode && currentNode?.type.includes('align_')) {
          finalPath = [...finalPath, 0];
        }
        Transforms.setNodes(
          editor,
          { type: isH3Enabled ? ELEMENT_PARAGRAPH : ELEMENT_H3 },
          { at: finalPath }
        );
      });
    },
    removeStandardCell({ editor, rowIndex, colIndex }) {
      const rowPath = [...self.tablePath, rowIndex];
      Transforms.unsetNodes(editor, ['standardCellId', 'isCollapsed'], { at: rowPath });
      Transforms.select(editor, [...rowPath, colIndex]);
      Transforms.collapse(editor, { edge: 'end' });
    },
    setStandardCell({ editor, cellId, rowIndex }) {
      const rowPath = [...self.tablePath, rowIndex];
      Transforms.setNodes(editor, { standardCellId: cellId, isCollapsed: true }, { at: rowPath });
    },
    expandStandardCell({ editor, rowIndex, colIndex }) {
      const rowPath = [...self.tablePath, rowIndex];
      Transforms.setNodes(editor, { isCollapsed: false }, { at: rowPath });
      Transforms.select(editor, [...rowPath, colIndex]);
      Transforms.collapse(editor, { edge: 'end' });
    },
    collapseStandardCell({ editor, rowIndex, colIndex }) {
      const rowPath = [...self.tablePath, rowIndex];
      Transforms.setNodes(editor, { isCollapsed: true }, { at: rowPath });
      Transforms.select(editor, [...rowPath, colIndex]);
      Transforms.collapse(editor, { edge: 'start' });
    },

    setCellColor({ editor, color }) {
      self.cellSelection.allSelectedCells.forEach((cellPath) => {
        Transforms.setNodes(editor, { backgroundColor: color }, { at: cellPath });
      });
    },
    removeCellColor({ editor }) {
      self.cellSelection.allSelectedCells.forEach((cellPath) => {
        Transforms.unsetNodes(editor, 'backgroundColor', { at: cellPath });
      });
    },

    mergeCell({ editor }) {
      self.cellSelection.mergeCell({ editor, tableId: self.id });
      self.updateSlateToCellWidth();
      self.syncRowColRef({ editor });
    },
    unMergeCell({ editor }) {
      self.cellSelection.unMergeCell({ editor, tableId: self.id });
      self.updateSlateToCellWidth();
      self.syncRowColRef({ editor });
    },
  }))
  .actions((self) => ({
    cancelConfirmMerge() {
      self.isConfirmMergeVisible = false;
    },
    onConfirmMergeCell({ editor }) {
      self.cellSelection.updateDragPosition({
        startDragPosition: self.tempStartDragPosition,
        endDragPosition: self.tempEndDragPosition,
      });
      self.mergeCell({ editor });
      self.isConfirmMergeVisible = false;

      self.tempStartDragPosition = null;
      self.tempEndDragPosition = null;
    },
    showConfirmMergeCell() {
      self.isConfirmMergeVisible = true;
      self.tempStartDragPosition = getSnapshot(self.cellSelection.startDragPosition);
      self.tempEndDragPosition = getSnapshot(self.cellSelection.endDragPosition);
    },
    updateTablePath(tablePath) {
      self.tablePath = tablePath;
    },
  }));

export const useAdminTableStore = () => {
  return useStore(TableAdminViewStore);
};
